/**
 * Stream B — B12: DSAR Request Handler
 *
 * Data Subject Access Request management.
 * Submit → collect all data → export → track SLA → complete.
 * Real API, no mocks. DB table: dsar_requests (14 columns).
 */
import { useState, useEffect, useCallback } from "react";
import { Shield, Plus, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { SkeletonCard } from "@/components/skeleton";
import { formatDate } from "@/lib/i18n";
import {
  CompliancePageHeader,
  ComplianceTenantRequiredState,
  ComplianceUnavailableState,
  useComplianceTenant,
} from "./shared";

interface DsarRequest {
  id: string;
  personId: string;
  requestType: "access" | "portability" | "rectification";
  status: "pending" | "in_progress" | "completed" | "rejected";
  requestedAt: string;
  deadlineAt: string;
  completedAt: string | null;
  resultPath: string | null;
  notes: string | null;
}

const STATUS_CONFIG = {
  pending: { bg: "bg-[var(--color-warning)]/10", text: "text-[var(--color-warning)]", Icon: Clock, label: "Pending" },
  in_progress: { bg: "bg-[var(--color-primary)]/10", text: "text-[var(--color-primary)]", Icon: Clock, label: "In progress" },
  completed: { bg: "bg-[var(--color-success)]/10", text: "text-[var(--color-success)]", Icon: CheckCircle, label: "Completed" },
  rejected: { bg: "bg-[var(--color-error)]/10", text: "text-[var(--color-error)]", Icon: AlertTriangle, label: "Rejected" },
};

function daysUntilDeadline(deadlineAt: string): number {
  return Math.max(0, Math.ceil((new Date(deadlineAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

export default function DsarPage() {
  const { tenantId, hasTenant, isLoading: isAuthLoading } = useComplianceTenant();
  const [requests, setRequests] = useState<DsarRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newPersonId, setNewPersonId] = useState("");
  const [newType, setNewType] = useState<DsarRequest["requestType"]>("access");
  const [newNotes, setNewNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailableMessage, setUnavailableMessage] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    if (!tenantId) {
      setRequests([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: DsarRequest[] }>(`/platform/compliance/dsar-requests?tenant_id=${tenantId}`);
      setRequests(res.data);
      setUnavailableMessage(null);
    } catch (err) {
      setRequests([]);
      if (err instanceof ApiError && err.status === 501) {
        setUnavailableMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : "Failed to load DSAR requests");
      }
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const handleSubmit = useCallback(async () => {
    if (!tenantId || !newPersonId.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await apiFetch("/platform/compliance/dsar-requests", {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          personId: newPersonId.trim(),
          requestType: newType,
          notes: newNotes.trim() || undefined,
        }),
      });
      setShowNew(false);
      setNewPersonId("");
      setNewNotes("");
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create DSAR request");
    } finally {
      setIsSubmitting(false);
    }
  }, [tenantId, newPersonId, newType, newNotes, loadRequests]);

  const urgentCount = requests.filter((r) => r.status === "pending" && daysUntilDeadline(r.deadlineAt) < 7).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <CompliancePageHeader
          icon={<Shield className="h-5 w-5 text-[var(--color-primary)]" />}
          title="Data Subject Access Requests"
          description="Uses the real api-platform GDPR contract. Submission requires a known person UUID for the active organization."
        />
        <button
          onClick={() => setShowNew(!showNew)}
          disabled={!hasTenant}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New Request
        </button>
      </div>

      {!isAuthLoading && !hasTenant ? <ComplianceTenantRequiredState /> : null}

      {error ? (
        <div className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      ) : null}

      {unavailableMessage ? (
        <ComplianceUnavailableState
          title="DSAR management is not available yet"
          description={unavailableMessage}
        />
      ) : null}

      {!hasTenant || unavailableMessage ? null : (
        <>
      {urgentCount > 0 && (
        <div className="rounded-lg border border-[var(--color-error)]/30 bg-[var(--color-error)]/5 px-4 py-3 flex items-center gap-2 text-sm text-[var(--color-error)]">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {urgentCount} request{urgentCount > 1 ? "s" : ""} due within 7 days!
        </div>
      )}

      {/* New request form */}
      {showNew && (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-3">
          <h3 className="font-medium text-[var(--color-foreground)]">Submit New DSAR</h3>
          <div className="flex gap-3">
            <input
              value={newPersonId}
              onChange={(e) => setNewPersonId(e.target.value)}
              placeholder="Contact UUID"
              className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            />
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as DsarRequest["requestType"])}
              className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            >
              <option value="access">Data Access (Art. 15)</option>
              <option value="portability">Data Portability (Art. 20)</option>
              <option value="rectification">Rectification (Art. 16)</option>
            </select>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !newPersonId.trim()}
              className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
          <textarea
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            placeholder="Optional notes for the request"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            rows={3}
          />
          <p className="text-xs text-[var(--color-muted-foreground)]">GDPR deadline: 30 days. This page no longer guesses by email because the canonical API requires a real person ID.</p>
        </div>
      )}

      {/* Request list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-16" />)}
        </div>
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Shield className="h-10 w-10 mx-auto text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-[var(--color-foreground)] font-medium">No DSAR requests</p>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">Requests appear when data subjects exercise their rights under GDPR/CCPA.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => {
            const status = STATUS_CONFIG[req.status];
            const StatusIcon = status.Icon;
            const daysLeft = daysUntilDeadline(req.deadlineAt);
            const isUrgent = req.status === "pending" && daysLeft < 7;

            return (
              <div key={req.id} className={`stagger-item rounded-xl border p-5 ${isUrgent ? "border-[var(--color-error)]/30 bg-[var(--color-error)]/5" : "border-[var(--color-border)] bg-[var(--color-background)]"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${status.bg} ${status.text}`}>
                      <StatusIcon className="h-3 w-3" /> {status.label}
                    </span>
                    <span className="text-sm font-medium text-[var(--color-foreground)]">{req.requestType.charAt(0).toUpperCase() + req.requestType.slice(1)} Request</span>
                    <span className="text-xs text-[var(--color-muted-foreground)] font-mono">{req.personId.slice(0, 8)}...</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {req.status !== "completed" && (
                      <span className={`text-sm font-medium ${isUrgent ? "text-[var(--color-error)]" : "text-[var(--color-muted-foreground)]"}`}>
                        {daysLeft}d remaining
                      </span>
                    )}
                    {req.resultPath && (
                      <span className="rounded-lg border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                        Export stored: {req.resultPath}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-2 text-xs text-[var(--color-muted-foreground)]">
                  Requested: {formatDate(req.requestedAt)} · Deadline: {formatDate(req.deadlineAt)}
                  {req.completedAt && ` · Completed: ${formatDate(req.completedAt)}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
        </>
      )}
    </div>
  );
}
